import {CAMPAIGN_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {DTNodeToClientModel} from 'common/src/modules/campaignVersions/utils/DTNodeToClientModel';
import {toClientFCCampaign} from 'common/src/modules/campaigns/campaigns.dataConverters';
import {
  CampaignWithDT,
  DTNode,
  DTVersion,
  ServerCampaignVersion,
  ServerDecisionTree,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import _map from 'lodash/map';
import {DateTime} from 'luxon';
import {
  CampaignMiniBrief,
  RemoteCampaignMiniBrief,
} from '../campaigns/campaignBrief/campaignBrief.types';

function dtVersionToClientModel({
  number,
  lastUpdate,
  createdDate,
  savedDate,
  name,
  activationStart,
  activationEnd,
  ...common
}: ServerCampaignVersion): DTVersion {
  return {
    ...common,
    name: name || `V${number}`,
    lastUpdate: DateTime.fromISO(lastUpdate),
    createdDate: DateTime.fromISO(createdDate),
    savedDate: DateTime.fromISO(savedDate),
    number: number,
    activationTime: {
      start: activationStart ? DateTime.fromISO(activationStart) : null,
      end: activationEnd ? DateTime.fromISO(activationEnd) : null,
    },
  };
}

function dtToClientModel({
  generalEnums,
  campaignEnums,
  decisionTree,
  relatedEnums,
  version,
}: {
  decisionTree: ServerDecisionTree;
  version?: ServerCampaignVersion;
  campaignEnums: Record<string, CLEnumsMapper>;
  generalEnums: Record<string, CLEnumsMapper>;
  relatedEnums: Record<string, CLEnumsMapper>;
}): CampaignWithDT {
  const campaign = toClientFCCampaign(decisionTree.campaignNode.campaign);
  const loadedVersion = version ? dtVersionToClientModel(version) : null;

  const nodesOrder = [..._map(decisionTree.nodes, 'id'), CAMPAIGN_NODE_ID];

  const nodesFromDB: DTNode[] = _map(decisionTree.nodes, (node) => {
    return DTNodeToClientModel(node, campaignEnums, generalEnums, relatedEnums);
  });

  const campaignNode = {
    x: decisionTree.campaignNode.x,
    y: decisionTree.campaignNode.y,
  };

  return {
    campaign,
    nodes: nodesFromDB,
    campaignNode,
    nodesOrder,
    loadedVersion,
    rootNodeId: decisionTree.rootNodeId,
    nextNodeId: decisionTree.nextNodeId,
    fallbackCreativeNodeId: decisionTree.fallbackCreativeNodeId,
  };
}

function toClientCampaignMiniBrief(remoteBrief: RemoteCampaignMiniBrief): CampaignMiniBrief {
  return {
    ...remoteBrief,
    startDate: remoteBrief.startDate ? DateTime.fromISO(remoteBrief.startDate) : null,
    endDate: remoteBrief.endDate ? DateTime.fromISO(remoteBrief.endDate) : null,
    audiences: _map(remoteBrief.audiences ?? [], (audience) => JSON.parse(audience)),
  };
}

function toServerCampaignMiniBrief(brief: CampaignMiniBrief) {
  return {
    ...brief,
    audiences: brief.audiences ? _map(brief.audiences, (audience) => JSON.stringify(audience)) : [],
  };
}

export {
  dtToClientModel,
  dtVersionToClientModel,
  toClientCampaignMiniBrief,
  toServerCampaignMiniBrief,
};
